import React, { useState, useEffect } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { Event, EventFormData, Office, Client, LegalFile, EventType, PlaceType, Court } from '../types';

interface EditEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (eventId: string, formData: EventFormData) => Promise<void>;
  event: Event | null;
  loading: boolean;
  offices: Office[];
  clients: Client[];
  legalFiles: LegalFile[];
  eventTypes: EventType[];
  placeTypes: PlaceType[];
  courts: Court[];
  language: 'en' | 'ar';
  strings: any;
}

const EditEventModal: React.FC<EditEventModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  event,
  loading,
  offices,
  clients,
  legalFiles,
  eventTypes,
  placeTypes,
  courts,
  language,
  strings
}) => {
  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    legalFileTitle: '',
    startDate: '',
    type: 'meeting',
    status: 1,
    officeId: '',
    lawyerId: '',
    officeID: '',
    officeName: '',
    clientID: '',
    clientName: '',
    clientPhoneNumber: '',
    legalFileID: '',
    eventTypeID: '',
    placeTypeID: '',
    courtID: '',
    placeName: '',
    date: '',
    time: language === 'ar' ? 'الوقت غير محدد' : 'Time not specified',
    notes: '',
    hasReminder: 0,
  });

  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && event) {
      setFormData({
        title: event.title || event.legalFileTitle || '',
        legalFileTitle: event.legalFileTitle || '',
        startDate: event.startDate || event.date || '',
        type: event.type || 'meeting',
        status: event.status,
        officeId: event.officeId || event.officeID || '',
        lawyerId: event.lawyerId || event.lawyerID || '',
        officeID: event.officeID || '',
        officeName: event.officeName || '',
        clientID: event.clientID || '',
        clientName: event.clientName || '',
        clientPhoneNumber: event.clientPhoneNumber || '',
        legalFileID: event.legalFileID || '',
        eventTypeID: event.eventTypeID || '',
        placeTypeID: event.placeTypeID || '',
        courtID: event.courtID || '',
        placeName: event.placeName || '',
        date: event.date || '',
        time: event.time || (language === 'ar' ? 'الوقت غير محدد' : 'Time not specified'),
        notes: event.notes || '',
        hasReminder: event.hasReminder || 0,
      });
      setValidationError(null);
    }
  }, [isOpen, event, language]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!event) return;

    // Validation
    if (!formData.officeID || !formData.clientID || !formData.legalFileID || 
        !formData.eventTypeID || !formData.placeTypeID || !formData.date) {
      setValidationError(language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill in all required fields');
      return;
    }

    try {
      await onSubmit(event.id, formData);
      onClose();
    } catch (error) {
      console.error('Error updating event:', error);
    }
  };

  const handleOfficeChange = (officeId: string) => {
    const selectedOffice = offices.find(office => office.id === officeId);
    setFormData(prev => ({
      ...prev,
      officeID: officeId,
      officeName: selectedOffice?.name || '',
    }));
  };

  const handleClientChange = (clientId: string) => {
    const selectedClient = clients.find(client => client.id === clientId);
    setFormData(prev => ({
      ...prev,
      clientID: clientId,
      clientName: selectedClient?.name || '',
      clientPhoneNumber: selectedClient?.phone || '',
    }));
  };

  const handleLegalFileChange = (fileId: string) => {
    const selectedFile = legalFiles.find(file => file.id === fileId);
    setFormData(prev => ({
      ...prev,
      legalFileID: fileId,
      legalFileTitle: selectedFile?.title || '',
    }));
  };

  const handlePlaceTypeChange = (placeTypeId: string) => {
    const selectedPlaceType = placeTypes.find(type => type.id === placeTypeId);
    const isCourt = selectedPlaceType?.name_en?.toLowerCase().includes('court') || placeTypeId === '1';
    
    setFormData(prev => ({
      ...prev,
      placeTypeID: placeTypeId,
      courtID: isCourt ? prev.courtID : '',
      placeName: isCourt ? '' : prev.placeName,
    }));
  };

  if (!isOpen || !event) return null;

  const isCourt = placeTypes.find(type => type.id === formData.placeTypeID)?.name_en?.toLowerCase().includes('court') || formData.placeTypeID === '1';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-white">
              {language === 'ar' ? 'تعديل الحدث' : 'Edit Event'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {validationError && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-red-600 dark:text-red-400 text-sm">{validationError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Office Selection */}
            <div>
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2 block">
                {language === 'ar' ? 'المكتب *' : 'Office *'}
              </label>
              <div className="relative">
                <select
                  value={formData.officeID}
                  onChange={(e) => handleOfficeChange(e.target.value)}
                  className={`w-full p-3 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary ${
                    !formData.officeID ? 'border-red-300 dark:border-red-600' : 'border-slate-300 dark:border-slate-600'
                  }`}
                  required
                >
                  <option value="">
                    {language === 'ar' ? 'اختر المكتب' : 'Select Office'}
                  </option>
                  {offices.map((office) => (
                    <option key={office.id} value={office.id}>
                      {office.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* Client Selection */}
            <div>
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2 block">
                {language === 'ar' ? 'العميل *' : 'Client *'}
              </label>
              <div className="relative">
                <select
                  value={formData.clientID}
                  onChange={(e) => handleClientChange(e.target.value)}
                  className={`w-full p-3 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary ${
                    !formData.clientID ? 'border-red-300 dark:border-red-600' : 'border-slate-300 dark:border-slate-600'
                  }`}
                  required
                >
                  <option value="">
                    {language === 'ar' ? 'اختر العميل' : 'Select Client'}
                  </option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* Legal File Selection */}
            <div>
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2 block">
                {language === 'ar' ? 'الملف القانوني *' : 'Legal File *'}
              </label>
              <div className="relative">
                <select
                  value={formData.legalFileID}
                  onChange={(e) => handleLegalFileChange(e.target.value)}
                  className={`w-full p-3 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary ${
                    !formData.legalFileID ? 'border-red-300 dark:border-red-600' : 'border-slate-300 dark:border-slate-600'
                  }`}
                  required
                >
                  <option value="">
                    {language === 'ar' ? 'اختر الملف القانوني' : 'Select Legal File'}
                  </option>
                  {legalFiles.map((file) => (
                    <option key={file.id} value={file.id}>
                      {file.title}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* Event Type */}
            <div>
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2 block">
                {language === 'ar' ? 'نوع الحدث *' : 'Event Type *'}
              </label>
              <div className="relative">
                <select
                  value={formData.eventTypeID}
                  onChange={(e) => setFormData(prev => ({ ...prev, eventTypeID: e.target.value }))}
                  className={`w-full p-3 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary ${
                    !formData.eventTypeID ? 'border-red-300 dark:border-red-600' : 'border-slate-300 dark:border-slate-600'
                  }`}
                  required
                >
                  <option value="">
                    {language === 'ar' ? 'اختر نوع الحدث' : 'Select Event Type'}
                  </option>
                  {eventTypes.map((eventType) => (
                    <option key={eventType.id} value={eventType.id}>
                      {language === 'ar' ? eventType.name_ar : eventType.name_en}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* Place Type */}
            <div>
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2 block">
                {language === 'ar' ? 'نوع مكان الحدث *' : 'Event Place Type *'}
              </label>
              <div className="relative">
                <select
                  value={formData.placeTypeID}
                  onChange={(e) => handlePlaceTypeChange(e.target.value)}
                  className={`w-full p-3 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary ${
                    !formData.placeTypeID ? 'border-red-300 dark:border-red-600' : 'border-slate-300 dark:border-slate-600'
                  }`}
                  required
                >
                  <option value="">
                    {language === 'ar' ? 'اختر نوع المكان' : 'Select Place Type'}
                  </option>
                  {placeTypes.map((placeType) => (
                    <option key={placeType.id} value={placeType.id}>
                      {language === 'ar' ? placeType.name_ar : placeType.name_en}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* Court Selection (if place type is court) */}
            {isCourt && (
              <div>
                <label className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2 block">
                  {language === 'ar' ? 'أسم المحكمة' : 'Court Name'}
                </label>
                <div className="relative">
                  <select
                    value={formData.courtID}
                    onChange={(e) => setFormData(prev => ({ ...prev, courtID: e.target.value }))}
                    className={`w-full p-3 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary ${
                      !formData.courtID ? 'border-red-300 dark:border-red-600' : 'border-slate-300 dark:border-slate-600'
                    }`}
                    required
                  >
                    <option value="">
                      {language === 'ar' ? 'اختر المحكمة' : 'Select Court'}
                    </option>
                    {courts.map((court) => (
                      <option key={court.id} value={court.id}>
                        {language === 'ar' ? court.name_ar : court.name_en}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                </div>
              </div>
            )}

            {/* Place Name (if not court) */}
            {!isCourt && (
              <div>
                <label className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2 block">
                  {language === 'ar' ? 'اسم المكان' : 'Place Name'}
                </label>
                <input
                  type="text"
                  value={formData.placeName}
                  onChange={(e) => setFormData(prev => ({ ...prev, placeName: e.target.value }))}
                  className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder={language === 'ar' ? 'أدخل اسم المكان' : 'Enter place name'}
                />
              </div>
            )}

            {/* Date */}
            <div>
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2 block">
                {language === 'ar' ? 'التاريخ *' : 'Date *'}
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                className={`w-full p-3 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary ${
                  !formData.date ? 'border-red-300 dark:border-red-600' : 'border-slate-300 dark:border-slate-600'
                }`}
                required
              />
            </div>

            {/* Time */}
            <div>
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2 block">
                {language === 'ar' ? 'الوقت' : 'Time'}
              </label>
              <input
                type="time"
                value={formData.time === (language === 'ar' ? 'الوقت غير محدد' : 'Time not specified') ? '' : formData.time}
                onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value || (language === 'ar' ? 'الوقت غير محدد' : 'Time not specified') }))}
                className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>

            {/* Status */}
            <div>
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2 block">
                {language === 'ar' ? 'الحالة' : 'Status'}
              </label>
              <div className="relative">
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: parseInt(e.target.value) }))}
                  className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value={1}>
                    {language === 'ar' ? 'قادم' : 'Upcoming'}
                  </option>
                  <option value={2}>
                    {language === 'ar' ? 'مكتمل' : 'Completed'}
                  </option>
                  <option value={3}>
                    {language === 'ar' ? 'ملغي' : 'Cancelled'}
                  </option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2 block">
              {language === 'ar' ? 'ملاحظات' : 'Notes'}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
              className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary"
              placeholder={language === 'ar' ? 'أدخل ملاحظات إضافية...' : 'Enter additional notes...'}
            />
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-slate-200 dark:border-slate-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading 
                ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...') 
                : (language === 'ar' ? 'حفظ التغييرات' : 'Save Changes')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEventModal;
